import type { NvsEntry, NvsPartition } from "./nvs.ts";

/** How the NVS table shows a partition's entries and their values. */

/** A string's first line up to this many characters, or a blob's first bytes, show in the table. */
const SHOWN_CHARACTERS = 80;
const SHOWN_BYTES = 16;
/** The tooltip holds the rest, up to these. */
const TITLE_CHARACTERS = 2000;
const TITLE_BYTES = 512;

export interface ValueCell {
    text: string;
    /** A dim note after the text: a string's or a blob's length; empty for a number. */
    note: string;
    title: string;
    /** Whether the text shows only part of the value. */
    cut: boolean;
}

/** A value's cell in the table. */
export function formatValue(entry: NvsEntry): ValueCell {
    const value = entry.value;
    if (typeof value === "string") {
        const first = value.split("\n")[0]!;
        const cut = first.length > SHOWN_CHARACTERS || first.length < value.length;
        return {
            text: cut ? `${first.slice(0, SHOWN_CHARACTERS)}…` : first,
            note: cut ? `${value.length} characters` : "",
            title: value.length > TITLE_CHARACTERS ? `${value.slice(0, TITLE_CHARACTERS)}…` : value,
            cut,
        };
    }
    if (value instanceof Uint8Array) {
        const shown = hexBytes(value.subarray(0, SHOWN_BYTES));
        const cut = value.length > SHOWN_BYTES;
        return {
            text: cut ? `${shown} …` : shown,
            note: `${value.length} bytes`,
            title: hexDump(value.subarray(0, TITLE_BYTES)) + (value.length > TITLE_BYTES ? "\n…" : ""),
            cut,
        };
    }
    const hex = typeof value === "bigint" || Number.isInteger(value) ? hexOf(entry) : undefined;
    return {
        text: String(value),
        note: "",
        title: hex === undefined ? String(value) : `${value} (${hex})`,
        cut: false,
    };
}

/** What the copy button copies: a string as it is, a blob in hex without spaces. */
export function copyText(value: string | Uint8Array): string {
    return typeof value === "string" ? value : hexBytes(value).replaceAll(" ", "");
}

/**
 * A string's or a blob's value as a file the editor opens: the string as
 * text, the blob as a hex dump. A key may hold a slash, which a file name cannot.
 */
export function valueFile(entry: NvsEntry & { value: string | Uint8Array }): { name: string; data: Uint8Array } {
    const key = entry.key.replaceAll("/", "%2F");
    return typeof entry.value === "string"
        ? { name: `${entry.namespace}/${key}.txt`, data: new TextEncoder().encode(entry.value) }
        : { name: `${entry.namespace}/${key}.hex.txt`, data: new TextEncoder().encode(hexDump(entry.value) + "\n") };
}

/** An integer as the bytes of its type hold it: -1 in an i8 is 0xff. */
function hexOf(entry: NvsEntry): string {
    const bits = Number(entry.type.slice(1));
    return `0x${BigInt.asUintN(bits, BigInt(entry.value as number | bigint)).toString(16)}`;
}

function hexBytes(bytes: Uint8Array): string {
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(" ");
}

/** Sixteen bytes a line, in hex and as ASCII. */
export function hexDump(bytes: Uint8Array): string {
    const lines: string[] = [];
    for (let at = 0; at < bytes.length; at += 16) {
        const line = bytes.subarray(at, at + 16);
        const text = Array.from(line, (byte) => (byte >= 0x20 && byte < 0x7f ? String.fromCharCode(byte) : ".")).join(
            "",
        );
        lines.push(`${at.toString(16).padStart(4, "0")}  ${hexBytes(line).padEnd(47)}  ${text}`);
    }
    return lines.join("\n");
}

/** The line above the table. */
export function summary(nvs: NvsPartition): string {
    const namespaces = new Set(nvs.entries.map((entry) => entry.namespace)).size;
    const pages = Object.entries(nvs.pages)
        .filter(([, count]) => count > 0)
        .map(([state, count]) => `${count} ${state}`)
        .join(", ");
    return [
        `${nvs.entries.length} ${nvs.entries.length === 1 ? "entry" : "entries"} in ${namespaces} ${namespaces === 1 ? "namespace" : "namespaces"}`,
        `format version ${nvs.version}`,
        `pages: ${pages}`,
        ...(nvs.skipped > 0
            ? [`${nvs.skipped} damaged or incomplete ${nvs.skipped === 1 ? "entry" : "entries"} skipped`]
            : []),
    ].join("; ");
}
